import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import classes from "./Newappointment.module.css";
import {
    FaTimes,
    FaUser,
    FaCalendarAlt,
    FaClock,
    FaCheckCircle,
    FaChevronDown,
    FaFileAlt,
    FaSpinner,
    FaSearch,
    FaUserMd,
    FaTimesCircle
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

const Newappointment = ({ isOpen, onClose, onAppointmentCreated }) => {
    const [formData, setFormData] = useState({
        patient_id: "",
        doctor_id: "",
        department_id: "",
        appointment_date: "",
        appointment_time: "",
        duration_minutes: 30,
        appointment_type: "New Consultation",
        visit_type: "OPD",
        location: "",
        room: "",
        priority: "Normal",
        reason: "",
        notes: "",
        reminder_channel: "SMS & Email",
        reminder_interval_hours: 24,
        send_confirmation: true,
        reminder_enabled: true,
    });

    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Patient search states
    const [patientSearchTerm, setPatientSearchTerm] = useState("");
    const [showPatientDropdown, setShowPatientDropdown] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const patientDropdownRef = useRef(null);

    // Doctor search states
    const [doctorSearchTerm, setDoctorSearchTerm] = useState("");
    const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const doctorDropdownRef = useRef(null);

    // Department search states
    const [departmentSearchTerm, setDepartmentSearchTerm] = useState("");
    const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const departmentDropdownRef = useRef(null);

    // Visit Type search states
    const [visitTypeSearchTerm, setVisitTypeSearchTerm] = useState("");
    const [showVisitTypeDropdown, setShowVisitTypeDropdown] = useState(false);
    const [selectedVisitType, setSelectedVisitType] = useState("OPD");
    const visitTypeDropdownRef = useRef(null);

    // Appointment Type search states
    const [apptTypeSearchTerm, setApptTypeSearchTerm] = useState("");
    const [showApptTypeDropdown, setShowApptTypeDropdown] = useState(false);
    const [selectedApptType, setSelectedApptType] = useState("New Consultation");
    const apptTypeDropdownRef = useRef(null);

    // Duration search states
    const [durationSearchTerm, setDurationSearchTerm] = useState("");
    const [showDurationDropdown, setShowDurationDropdown] = useState(false);
    const [selectedDuration, setSelectedDuration] = useState(30);
    const durationDropdownRef = useRef(null);

    // Priority search states
    const [prioritySearchTerm, setPrioritySearchTerm] = useState("");
    const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
    const [selectedPriority, setSelectedPriority] = useState("Normal");
    const priorityDropdownRef = useRef(null);

    // Reminder Channel search states
    const [reminderSearchTerm, setReminderSearchTerm] = useState("");
    const [showReminderDropdown, setShowReminderDropdown] = useState(false);
    const [selectedReminder, setSelectedReminder] = useState("SMS & Email");
    const reminderDropdownRef = useRef(null);

    // Reminder Interval search states
    const [intervalSearchTerm, setIntervalSearchTerm] = useState("");
    const [showIntervalDropdown, setShowIntervalDropdown] = useState(false);
    const [selectedInterval, setSelectedInterval] = useState(24);
    const intervalDropdownRef = useRef(null);

    const visitTypeOptions = [
        { value: "OPD", label: "OPD - Outpatient" },
        { value: "IPD", label: "IPD - Inpatient" },
        { value: "Emergency", label: "Emergency" }
    ];

    const appointmentTypeOptions = [
        { value: "New Consultation", label: "New Consultation" },
        { value: "Follow-up", label: "Follow-up" },
        { value: "Check-up", label: "Check-up" }
    ];

    const durationOptions = [
        { value: 15, label: "15 Minutes" },
        { value: 30, label: "30 Minutes" },
        { value: 45, label: "45 Minutes" },
        { value: 60, label: "60 Minutes" }
    ];

    const priorityOptions = [
        { value: "Normal", label: "Normal" },
        { value: "High", label: "High" },
        { value: "Emergency", label: "Emergency" }
    ];

    const reminderOptions = [
        { value: "SMS & Email", label: "SMS & Email" },
        { value: "SMS", label: "SMS Only" },
        { value: "Email", label: "Email Only" },
        { value: "None", label: "None" }
    ];

    // Only valid values: 24 or null
    const intervalOptions = [
        { value: 24, label: "1 Day Before" },
        { value: null, label: "No Reminder" }
    ];

    // Sample patient data for display
    const samplePatient = {
        first_name: "John",
        last_name: "Doe",
        patient_no: "OPD - 12345",
        gender: "Male",
        age: "34",
        phone: "+1 987 654 3210",
        email: "johndoe@email.com"
    };

    // Get company ID
    const getCompanyId = () => {
        return parseInt(localStorage.getItem("companyId") || "1");
    };

    // Fetch data when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchPatients();
            fetchDoctors();
            fetchDepartments();
        }
    }, [isOpen]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (patientDropdownRef.current && !patientDropdownRef.current.contains(event.target)) {
                setShowPatientDropdown(false);
            }
            if (doctorDropdownRef.current && !doctorDropdownRef.current.contains(event.target)) {
                setShowDoctorDropdown(false);
            }
            if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target)) {
                setShowDepartmentDropdown(false);
            }
            if (visitTypeDropdownRef.current && !visitTypeDropdownRef.current.contains(event.target)) {
                setShowVisitTypeDropdown(false);
            }
            if (apptTypeDropdownRef.current && !apptTypeDropdownRef.current.contains(event.target)) {
                setShowApptTypeDropdown(false);
            }
            if (durationDropdownRef.current && !durationDropdownRef.current.contains(event.target)) {
                setShowDurationDropdown(false);
            }
            if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(event.target)) {
                setShowPriorityDropdown(false);
            }
            if (reminderDropdownRef.current && !reminderDropdownRef.current.contains(event.target)) {
                setShowReminderDropdown(false);
            }
            if (intervalDropdownRef.current && !intervalDropdownRef.current.contains(event.target)) {
                setShowIntervalDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchPatients = async () => {
        try {
            const companyId = getCompanyId();
            const response = await axios.get(`${BASE_URL}/api/patient`, {
                params: { company_id: companyId },
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
            });
            if (response.data && response.data.data) {
                const data = response.data.data.data || response.data.data;
                setPatients(Array.isArray(data) ? data : [data]);
            }
        } catch (error) {
            console.error("Error fetching patients:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch patients. Please try again.',
                confirmButtonColor: '#2F80ED',
            });
        }
    };

    const fetchDoctors = async () => {
        try {
            const companyId = getCompanyId();
            const response = await axios.get(`${BASE_URL}/api/doctor`, {
                params: { company_id: companyId },
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
            });
            if (response.data && response.data.data) {
                const data = response.data.data.data || response.data.data;
                setDoctors(Array.isArray(data) ? data : [data]);
            }
        } catch (error) {
            console.error("Error fetching doctors:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch doctors. Please try again.',
                confirmButtonColor: '#2F80ED',
            });
        }
    };

    const fetchDepartments = async () => {
        try {
            const companyId = getCompanyId();
            const response = await axios.get(`${BASE_URL}/api/department`, {
                params: { company_id: companyId },
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
            });
            console.log("Departments API Response:", response.data);

            if (response.data && response.data.data) {
                let departmentsData = response.data.data;
                if (departmentsData.data) {
                    departmentsData = departmentsData.data;
                }
                if (!Array.isArray(departmentsData)) {
                    departmentsData = [departmentsData];
                }
                setDepartments(departmentsData);
            } else {
                setDepartments([]);
            }
        } catch (error) {
            console.error("Error fetching departments:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch departments. Please try again.',
                confirmButtonColor: '#2F80ED',
            });
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError("");
        setSuccess("");
    };

    // Patient handlers
    const handlePatientSelect = (patient) => {
        setSelectedPatient(patient);
        setFormData(prev => ({
            ...prev,
            patient_id: patient.id
        }));
        setPatientSearchTerm(`${patient.first_name} ${patient.last_name} - ${patient.patient_no || ''}`);
        setShowPatientDropdown(false);
        setError("");
    };

    const handlePatientSearchChange = (e) => {
        const value = e.target.value;
        setPatientSearchTerm(value);
        setShowPatientDropdown(true);
        if (!value) {
            setSelectedPatient(null);
            setFormData(prev => ({ ...prev, patient_id: "" }));
        }
    };

    const clearPatientSelection = () => {
        setSelectedPatient(null);
        setPatientSearchTerm("");
        setFormData(prev => ({ ...prev, patient_id: "" }));
        setShowPatientDropdown(false);
    };

    // Doctor handlers
    const handleDoctorSelect = (doctor) => {
        setSelectedDoctor(doctor);
        setFormData(prev => ({
            ...prev,
            doctor_id: doctor.id,
            department_id: doctor.department_id || ""
        }));
        setDoctorSearchTerm(`Dr. ${doctor.first_name} ${doctor.last_name}`);
        setShowDoctorDropdown(false);
        setError("");
    };

    const handleDoctorSearchChange = (e) => {
        const value = e.target.value;
        setDoctorSearchTerm(value);
        setShowDoctorDropdown(true);
        if (!value) {
            setSelectedDoctor(null);
            setFormData(prev => ({ ...prev, doctor_id: "" }));
        }
    };

    const clearDoctorSelection = () => {
        setSelectedDoctor(null);
        setDoctorSearchTerm("");
        setFormData(prev => ({ ...prev, doctor_id: "" }));
        setShowDoctorDropdown(false);
    };

    // Department handlers
    const handleDepartmentSelect = (dept) => {
        setSelectedDepartment(dept);
        setFormData(prev => ({
            ...prev,
            department_id: dept.id
        }));
        setDepartmentSearchTerm(dept.name);
        setShowDepartmentDropdown(false);
        setError("");
    };

    const handleDepartmentSearchChange = (e) => {
        const value = e.target.value;
        setDepartmentSearchTerm(value);
        setShowDepartmentDropdown(true);
        if (!value) {
            setSelectedDepartment(null);
            setFormData(prev => ({ ...prev, department_id: "" }));
        }
    };

    const clearDepartmentSelection = () => {
        setSelectedDepartment(null);
        setDepartmentSearchTerm("");
        setFormData(prev => ({ ...prev, department_id: "" }));
        setShowDepartmentDropdown(false);
    };

    // Generic dropdown handlers
    const handleOptionSelect = (field, value, label, setSearch, setShow, setSelected) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setSearch(label);
        setShow(false);
        if (setSelected) setSelected(value);
        setError("");
    };

    const handleOptionSearchChange = (e, setSearch, setShow) => {
        const value = e.target.value;
        setSearch(value);
        setShow(true);
    };

    const handleClearSelection = (setSearch, setFormField, field, setSelected) => {
        setSearch("");
        setFormData(prev => ({ ...prev, [field]: "" }));
        if (setSelected) setSelected(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        setSubmitting(true);

        const errors = [];
        if (!formData.patient_id) errors.push("Please select a patient");
        if (!formData.doctor_id) errors.push("Please select a doctor");
        if (!formData.department_id) errors.push("Please select a department");
        if (!formData.appointment_date) errors.push("Appointment date is required");
        if (!formData.appointment_time) errors.push("Appointment time is required");
        if (!formData.reason) errors.push("Reason for visit is required");

        if (errors.length > 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                html: `<div style="text-align: left;">${errors.map((e, i) => `${i + 1}. ${e}`).join('<br>')}</div>`,
                confirmButtonColor: '#2F80ED',
            });
            setSubmitting(false);
            return;
        }

        try {
            const companyId = getCompanyId();

            // Get the date and time values
            const dateValue = formData.appointment_date; // "2026-09-04"
            const timeValue = formData.appointment_time; // "14:00"

            if (!dateValue || !timeValue) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid Date/Time',
                    text: 'Please select both date and time.',
                    confirmButtonColor: '#2F80ED',
                });
                setSubmitting(false);
                return;
            }

            // Format appointment_time as HH:mm
            let formattedTime = timeValue;
            if (formattedTime.includes(':')) {
                const parts = formattedTime.split(':');
                formattedTime = `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
            }

            // Map reminder_channel to reminder_type
            let reminderType = "SMS & Email";
            if (formData.reminder_channel === "SMS") reminderType = "SMS";
            else if (formData.reminder_channel === "Email") reminderType = "Email";
            else if (formData.reminder_channel === "None") reminderType = "None";
            else if (formData.reminder_channel === "SMS & Email") reminderType = "SMS & Email";

            // Map reminder_interval_hours to advance_notice
            let advanceNotice = "1 Day Before";
            if (formData.reminder_interval_hours === 24) advanceNotice = "1 Day Before";
            else if (formData.reminder_interval_hours === 48) advanceNotice = "2 Days Before";
            else if (formData.reminder_interval_hours === null) advanceNotice = "None";

            const payload = {
                company_id: companyId,
                patient_id: parseInt(formData.patient_id),
                doctor_id: parseInt(formData.doctor_id),
                department_id: parseInt(formData.department_id),
                appointment_date: dateValue,
                appointment_time: formattedTime,
                duration_minutes: parseInt(formData.duration_minutes),
                appointment_type: formData.appointment_type,
                visit_type: formData.visit_type,
                location: formData.location || "OPD",
                room: formData.room || "Room 2",
                priority: formData.priority || "Normal",
                reason: formData.reason,
                notes: formData.notes || "",
                reminder_type: reminderType,
                advance_notice: advanceNotice,
                send_confirmation: formData.send_confirmation,
            };

            console.log("Creating Appointment with payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/api/appointment/create`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    },
                }
            );

            console.log("Appointment Created:", response.data);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Appointment created successfully!',
                confirmButtonColor: '#2F80ED',
                timer: 2000,
                timerProgressBar: true,
            });

            setSuccess("Appointment created successfully!");

            // Reset form
            setFormData({
                patient_id: "",
                doctor_id: "",
                department_id: "",
                appointment_date: "",
                appointment_time: "",
                duration_minutes: 30,
                appointment_type: "New Consultation",
                visit_type: "OPD",
                location: "",
                room: "",
                priority: "Normal",
                reason: "",
                notes: "",
                reminder_channel: "SMS & Email",
                reminder_interval_hours: 24,
                send_confirmation: true,
                reminder_enabled: true,
            });
            setPatientSearchTerm("");
            setSelectedPatient(null);
            setDoctorSearchTerm("");
            setSelectedDoctor(null);
            setDepartmentSearchTerm("");
            setSelectedDepartment(null);
            setVisitTypeSearchTerm("");
            setApptTypeSearchTerm("");
            setDurationSearchTerm("");
            setPrioritySearchTerm("");
            setReminderSearchTerm("");
            setIntervalSearchTerm("");

            if (onAppointmentCreated) {
                onAppointmentCreated(response.data.data);
            }

            setTimeout(() => {
                onClose();
            }, 1500);

        } catch (error) {
            console.error("Error creating appointment:", error);

            let errorMsg = "Failed to create appointment. Please try again.";

            if (error.response) {
                if (error.response.status === 401) {
                    errorMsg = "Unauthorized. Please login again.";
                } else if (error.response.data?.message) {
                    const message = error.response.data.message;
                    if (typeof message === 'object') {
                        const firstKey = Object.keys(message)[0];
                        if (firstKey) {
                            if (Array.isArray(message[firstKey])) {
                                errorMsg = message[firstKey][0];
                            } else {
                                errorMsg = message[firstKey] || JSON.stringify(message);
                            }
                        } else {
                            errorMsg = "Please check your input and try again.";
                        }
                    } else if (typeof message === 'string') {
                        errorMsg = message;
                    }
                } else if (error.response.data?.errors) {
                    const errors = error.response.data.errors;
                    const firstKey = Object.keys(errors)[0];
                    if (firstKey && errors[firstKey][0]) {
                        errorMsg = errors[firstKey][0];
                    }
                }
            } else if (error.request) {
                errorMsg = "Unable to connect to server. Please check your internet connection.";
            }

            setError(errorMsg);

            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: errorMsg,
                confirmButtonColor: '#2F80ED',
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Filter patients based on search
    const filteredPatients = patients.filter(patient => {
        if (!patientSearchTerm) return true;
        const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.toLowerCase();
        const search = patientSearchTerm.toLowerCase();
        return fullName.includes(search) ||
            (patient.patient_no && patient.patient_no.toLowerCase().includes(search)) ||
            (patient.phone && patient.phone.toLowerCase().includes(search));
    });

    // Filter doctors based on search
    const filteredDoctors = doctors.filter(doctor => {
        if (!doctorSearchTerm) return true;
        const fullName = `${doctor.first_name || ''} ${doctor.last_name || ''}`.toLowerCase();
        const search = doctorSearchTerm.toLowerCase();
        return fullName.includes(search) ||
            (doctor.specialization && doctor.specialization.toLowerCase().includes(search));
    });

    // Filter departments based on search
    const filteredDepartments = departments.filter(dept => {
        if (!departmentSearchTerm) return true;
        return dept.name?.toLowerCase().includes(departmentSearchTerm.toLowerCase());
    });

    // Generic filter function for options
    const filterOptions = (options, searchTerm, labelKey = 'label') => {
        if (!searchTerm) return options;
        return options.filter(opt =>
            opt[labelKey]?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    // Generic dropdown component
    const SearchableDropdown = ({
        label,
        required,
        placeholder,
        searchTerm,
        setSearchTerm,
        showDropdown,
        setShowDropdown,
        dropdownRef,
        options,
        selectedValue,
        onSelect,
        onClear,
        displayKey = 'label',
        valueKey = 'value',
        icon,
        clearable = true,
        showClearButton = true
    }) => {
        const filtered = filterOptions(options, searchTerm, displayKey);

        return (
            <div className={classes.formGroup}>
                <label>{label} {required && <span className={classes.required}>*</span>}</label>
                <div className={classes.searchSelect} ref={dropdownRef}>
                    <div className={classes.searchWrapper}>
                        {icon && <span className={classes.searchIcon}>{icon}</span>}
                        <input
                            type="text"
                            placeholder={placeholder}
                            className={classes.searchInput}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setShowDropdown(true);
                            }}
                            onFocus={() => setShowDropdown(true)}
                            onClick={() => setShowDropdown(true)}
                            autoComplete="off"
                        />
                        {clearable && selectedValue && showClearButton && (
                            <button
                                type="button"
                                className={classes.clearBtn}
                                onClick={onClear}
                            >
                                <FaTimesCircle />
                            </button>
                        )}
                        <span className={classes.dropIcon}><FaChevronDown /></span>
                    </div>
                    {showDropdown && (
                        <div className={classes.dropdownList}>
                            {filtered.length > 0 ? (
                                filtered.map((option) => (
                                    <div
                                        key={option[valueKey] !== undefined ? option[valueKey] : option.id || option.value}
                                        className={`${classes.dropdownItem} ${selectedValue === option[valueKey] ? classes.selectedItem : ''}`}
                                        onClick={() => {
                                            onSelect(option);
                                            setShowDropdown(false);
                                        }}
                                    >
                                        <span className={classes.optionLabel}>
                                            {option[displayKey] || option.name || option.label}
                                        </span>
                                        {selectedValue === option[valueKey] && (
                                            <span className={classes.checkMark}>✓</span>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className={classes.noResults}>No options found</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className={classes.modalOverlay}>
            <div className={classes.modalContainer}>

                {/* HEADER */}
                <div className={classes.modalHeader}>
                    <div className={classes.headerLeft}>
                        <div className={classes.headerIcon}>
                            <FaFileAlt />
                        </div>
                        <div>
                            <h2>New Appointment</h2>
                            <p>Schedule a new appointment for a patient</p>
                        </div>
                    </div>
                    <button className={classes.closeBtn} onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={classes.form}>
                    <div className={classes.modalBody}>

                        {/* Error/Success Messages */}
                        {error && (
                            <div className={classes.errorMessage}>
                                <span>{error}</span>
                            </div>
                        )}
                        {success && (
                            <div className={classes.successMessage}>
                                <span>{success}</span>
                            </div>
                        )}

                        {/* SECTION 1: PATIENT INFORMATION */}
                        <div className={classes.formSection}>
                            <h3 className={classes.sectionTitle}>Patient Information</h3>
                            <div className={classes.grid2Col}>
                                <SearchableDropdown
                                    label="Patient"
                                    required={true}
                                    placeholder="Search by name, UHID, or phone..."
                                    searchTerm={patientSearchTerm}
                                    setSearchTerm={setPatientSearchTerm}
                                    showDropdown={showPatientDropdown}
                                    setShowDropdown={setShowPatientDropdown}
                                    dropdownRef={patientDropdownRef}
                                    options={filteredPatients}
                                    selectedValue={selectedPatient?.id}
                                    onSelect={(patient) => {
                                        setSelectedPatient(patient);
                                        setFormData(prev => ({ ...prev, patient_id: patient.id }));
                                        setPatientSearchTerm(`${patient.first_name} ${patient.last_name} - ${patient.patient_no || ''}`);
                                        setShowPatientDropdown(false);
                                        setError("");
                                    }}
                                    onClear={() => {
                                        setSelectedPatient(null);
                                        setPatientSearchTerm("");
                                        setFormData(prev => ({ ...prev, patient_id: "" }));
                                        setShowPatientDropdown(false);
                                    }}
                                    displayKey="name"
                                    valueKey="id"
                                    icon={<FaSearch />}
                                />
                                <div className={classes.formGroup}>
                                    <label>Contact Number</label>
                                    <input
                                        type="text"
                                        name="contact"
                                        value={selectedPatient?.phone || formData.contact || ""}
                                        onChange={handleChange}
                                        placeholder="Contact number"
                                        className={classes.formInput}
                                        readOnly={!!selectedPatient}
                                    />
                                </div>
                            </div>

                            {/* SELECTED PATIENT CARD */}
                            <div className={classes.patientCard}>
                                <div className={classes.patientAvatar}>
                                    <FaUser />
                                </div>
                                <div className={classes.patientInfo}>
                                    {selectedPatient ? (
                                        <>
                                            <h4>{selectedPatient.first_name} {selectedPatient.last_name}</h4>
                                            <p>
                                                {selectedPatient.patient_no || 'N/A'}
                                                {selectedPatient.gender && ` | ${selectedPatient.gender}`}
                                                {selectedPatient.age && ` | ${selectedPatient.age} Y`}
                                                {selectedPatient.phone && ` | ${selectedPatient.phone}`}
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <h4>John Doe</h4>
                                            <p>OPD - 12345 &nbsp;|&nbsp; 34 Y / Male</p>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className={classes.grid2Col} style={{ marginTop: '16px' }}>
                                <div className={classes.formGroup}>
                                    <label>Email (Optional)</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={selectedPatient?.email || formData.email || ""}
                                        onChange={handleChange}
                                        placeholder="Email address"
                                        className={classes.formInput}
                                        readOnly={!!selectedPatient}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* SECTION 2: APPOINTMENT DETAILS */}
                        <div className={classes.formSection}>
                            <h3 className={classes.sectionTitle}>Appointment Details</h3>

                            <div className={classes.grid3Col}>
                                <SearchableDropdown
                                    label="Department"
                                    required={true}
                                    placeholder="Search department..."
                                    searchTerm={departmentSearchTerm}
                                    setSearchTerm={setDepartmentSearchTerm}
                                    showDropdown={showDepartmentDropdown}
                                    setShowDropdown={setShowDepartmentDropdown}
                                    dropdownRef={departmentDropdownRef}
                                    options={filteredDepartments}
                                    selectedValue={selectedDepartment?.id}
                                    onSelect={(dept) => {
                                        setSelectedDepartment(dept);
                                        setFormData(prev => ({ ...prev, department_id: dept.id }));
                                        setDepartmentSearchTerm(dept.name);
                                        setShowDepartmentDropdown(false);
                                        setError("");
                                    }}
                                    onClear={() => {
                                        setSelectedDepartment(null);
                                        setDepartmentSearchTerm("");
                                        setFormData(prev => ({ ...prev, department_id: "" }));
                                        setShowDepartmentDropdown(false);
                                    }}
                                    displayKey="name"
                                    valueKey="id"
                                    icon={<FaSearch />}
                                />

                                <SearchableDropdown
                                    label="Doctor"
                                    required={true}
                                    placeholder="Search doctor by name..."
                                    searchTerm={doctorSearchTerm}
                                    setSearchTerm={setDoctorSearchTerm}
                                    showDropdown={showDoctorDropdown}
                                    setShowDropdown={setShowDoctorDropdown}
                                    dropdownRef={doctorDropdownRef}
                                    options={filteredDoctors}
                                    selectedValue={selectedDoctor?.id}
                                    onSelect={(doctor) => {
                                        setSelectedDoctor(doctor);
                                        setFormData(prev => ({
                                            ...prev,
                                            doctor_id: doctor.id,
                                            department_id: doctor.department_id || prev.department_id
                                        }));
                                        setDoctorSearchTerm(`Dr. ${doctor.first_name} ${doctor.last_name}`);
                                        setShowDoctorDropdown(false);
                                        setError("");
                                    }}
                                    onClear={() => {
                                        setSelectedDoctor(null);
                                        setDoctorSearchTerm("");
                                        setFormData(prev => ({ ...prev, doctor_id: "" }));
                                        setShowDoctorDropdown(false);
                                    }}
                                    displayKey="name"
                                    valueKey="id"
                                    icon={<FaUserMd />}
                                />

                                <SearchableDropdown
                                    label="Visit Type"
                                    required={true}
                                    placeholder="Search visit type..."
                                    searchTerm={visitTypeSearchTerm}
                                    setSearchTerm={setVisitTypeSearchTerm}
                                    showDropdown={showVisitTypeDropdown}
                                    setShowDropdown={setShowVisitTypeDropdown}
                                    dropdownRef={visitTypeDropdownRef}
                                    options={visitTypeOptions}
                                    selectedValue={formData.visit_type}
                                    onSelect={(option) => {
                                        setFormData(prev => ({ ...prev, visit_type: option.value }));
                                        setVisitTypeSearchTerm(option.label);
                                        setShowVisitTypeDropdown(false);
                                        setError("");
                                    }}
                                    onClear={() => {
                                        setFormData(prev => ({ ...prev, visit_type: "OPD" }));
                                        setVisitTypeSearchTerm("");
                                        setShowVisitTypeDropdown(false);
                                    }}
                                    displayKey="label"
                                    valueKey="value"
                                    showClearButton={false}
                                />
                            </div>

                            <div className={classes.grid3Col}>
                                <div className={classes.formGroup}>
                                    <label>Appointment Date <span className={classes.required}>*</span></label>
                                    <div className={classes.inputWithIcon}>
                                        <FaCalendarAlt className={classes.inputIcon} />
                                        <input
                                            type="date"
                                            name="appointment_date"
                                            value={formData.appointment_date}
                                            onChange={handleChange}
                                            className={classes.formInput}
                                            min={new Date().toISOString().split('T')[0]}
                                        />
                                    </div>
                                </div>
                                <div className={classes.formGroup}>
                                    <label>Appointment Time <span className={classes.required}>*</span></label>
                                    <div className={classes.inputWithIcon}>
                                        <FaClock className={classes.inputIcon} />
                                        <input
                                            type="time"
                                            name="appointment_time"
                                            value={formData.appointment_time}
                                            onChange={handleChange}
                                            className={classes.formInput}
                                        />
                                    </div>
                                </div>
                                <SearchableDropdown
                                    label="Duration"
                                    required={false}
                                    placeholder="Search duration..."
                                    searchTerm={durationSearchTerm}
                                    setSearchTerm={setDurationSearchTerm}
                                    showDropdown={showDurationDropdown}
                                    setShowDropdown={setShowDurationDropdown}
                                    dropdownRef={durationDropdownRef}
                                    options={durationOptions}
                                    selectedValue={formData.duration_minutes}
                                    onSelect={(option) => {
                                        setFormData(prev => ({ ...prev, duration_minutes: option.value }));
                                        setDurationSearchTerm(option.label);
                                        setShowDurationDropdown(false);
                                        setError("");
                                    }}
                                    onClear={() => {
                                        setFormData(prev => ({ ...prev, duration_minutes: 30 }));
                                        setDurationSearchTerm("");
                                        setShowDurationDropdown(false);
                                    }}
                                    displayKey="label"
                                    valueKey="value"
                                    showClearButton={false}
                                />
                            </div>

                            <div className={classes.grid3Col}>
                                <div className={classes.formGroup}>
                                    <label>Location</label>
                                    <input
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        placeholder="Enter location"
                                        className={classes.formInput}
                                    />
                                </div>
                                <div className={classes.formGroup}>
                                    <label>Room</label>
                                    <input
                                        type="text"
                                        name="room"
                                        value={formData.room}
                                        onChange={handleChange}
                                        placeholder="Enter room number"
                                        className={classes.formInput}
                                    />
                                </div>
                                <SearchableDropdown
                                    label="Appointment Type"
                                    required={false}
                                    placeholder="Search appointment type..."
                                    searchTerm={apptTypeSearchTerm}
                                    setSearchTerm={setApptTypeSearchTerm}
                                    showDropdown={showApptTypeDropdown}
                                    setShowDropdown={setShowApptTypeDropdown}
                                    dropdownRef={apptTypeDropdownRef}
                                    options={appointmentTypeOptions}
                                    selectedValue={formData.appointment_type}
                                    onSelect={(option) => {
                                        setFormData(prev => ({ ...prev, appointment_type: option.value }));
                                        setApptTypeSearchTerm(option.label);
                                        setShowApptTypeDropdown(false);
                                        setError("");
                                    }}
                                    onClear={() => {
                                        setFormData(prev => ({ ...prev, appointment_type: "New Consultation" }));
                                        setApptTypeSearchTerm("");
                                        setShowApptTypeDropdown(false);
                                    }}
                                    displayKey="label"
                                    valueKey="value"
                                    showClearButton={false}
                                />
                            </div>
                        </div>

                        {/* SECTION 3: REASON & NOTES */}
                        <div className={classes.formSection}>
                            <h3 className={classes.sectionTitle}>Reason & Notes</h3>
                            <div className={classes.grid2Col}>
                                <div className={classes.formGroup}>
                                    <label>Reason for Visit <span className={classes.required}>*</span></label>
                                    <textarea
                                        name="reason"
                                        value={formData.reason}
                                        onChange={handleChange}
                                        className={classes.formTextarea}
                                        rows="4"
                                        placeholder="Enter reason for visit (max 500 characters)"
                                        maxLength="500"
                                    />
                                    <div className={classes.charCount}>{formData.reason.length} / 500</div>
                                </div>
                                <div className={classes.formGroup}>
                                    <label>Notes (Optional)</label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        className={classes.formTextarea}
                                        rows="4"
                                        placeholder="Add any additional notes (max 500 characters)"
                                        maxLength="500"
                                    />
                                    <div className={classes.charCount}>{formData.notes.length} / 500</div>
                                </div>
                            </div>
                        </div>

                        {/* SECTION 4: REMINDERS & PREFERENCES */}
                        <div className={classes.formSection}>
                            <h3 className={classes.sectionTitle}>Reminders & Preferences</h3>
                            <div className={classes.grid3Col}>
                                <SearchableDropdown
                                    label="Reminder Channel"
                                    required={false}
                                    placeholder="Search reminder channel..."
                                    searchTerm={reminderSearchTerm}
                                    setSearchTerm={setReminderSearchTerm}
                                    showDropdown={showReminderDropdown}
                                    setShowDropdown={setShowReminderDropdown}
                                    dropdownRef={reminderDropdownRef}
                                    options={reminderOptions}
                                    selectedValue={formData.reminder_channel}
                                    onSelect={(option) => {
                                        setFormData(prev => ({ ...prev, reminder_channel: option.value }));
                                        setReminderSearchTerm(option.label);
                                        setShowReminderDropdown(false);
                                        setError("");
                                    }}
                                    onClear={() => {
                                        setFormData(prev => ({ ...prev, reminder_channel: "SMS & Email" }));
                                        setReminderSearchTerm("");
                                        setShowReminderDropdown(false);
                                    }}
                                    displayKey="label"
                                    valueKey="value"
                                    showClearButton={false}
                                />

                                <SearchableDropdown
                                    label="Reminder Interval"
                                    required={false}
                                    placeholder="Search reminder interval..."
                                    searchTerm={intervalSearchTerm}
                                    setSearchTerm={setIntervalSearchTerm}
                                    showDropdown={showIntervalDropdown}
                                    setShowDropdown={setShowIntervalDropdown}
                                    dropdownRef={intervalDropdownRef}
                                    options={intervalOptions}
                                    selectedValue={formData.reminder_interval_hours}
                                    onSelect={(option) => {
                                        setFormData(prev => ({ ...prev, reminder_interval_hours: option.value }));
                                        setIntervalSearchTerm(option.label);
                                        setShowIntervalDropdown(false);
                                        setError("");
                                    }}
                                    onClear={() => {
                                        setFormData(prev => ({ ...prev, reminder_interval_hours: 24 }));
                                        setIntervalSearchTerm("");
                                        setShowIntervalDropdown(false);
                                    }}
                                    displayKey="label"
                                    valueKey="value"
                                    showClearButton={false}
                                />

                                <SearchableDropdown
                                    label="Priority"
                                    required={false}
                                    placeholder="Search priority..."
                                    searchTerm={prioritySearchTerm}
                                    setSearchTerm={setPrioritySearchTerm}
                                    showDropdown={showPriorityDropdown}
                                    setShowDropdown={setShowPriorityDropdown}
                                    dropdownRef={priorityDropdownRef}
                                    options={priorityOptions}
                                    selectedValue={formData.priority}
                                    onSelect={(option) => {
                                        setFormData(prev => ({ ...prev, priority: option.value }));
                                        setPrioritySearchTerm(option.label);
                                        setShowPriorityDropdown(false);
                                        setError("");
                                    }}
                                    onClear={() => {
                                        setFormData(prev => ({ ...prev, priority: "Normal" }));
                                        setPrioritySearchTerm("");
                                        setShowPriorityDropdown(false);
                                    }}
                                    displayKey="label"
                                    valueKey="value"
                                    showClearButton={false}
                                />
                            </div>

                            <div className={classes.checkboxGroup}>
                                <label className={classes.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="send_confirmation"
                                        checked={formData.send_confirmation}
                                        onChange={handleChange}
                                    />
                                    Send appointment confirmation to patient
                                </label>
                            </div>
                            <div className={classes.checkboxGroup}>
                                <label className={classes.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="reminder_enabled"
                                        checked={formData.reminder_enabled}
                                        onChange={handleChange}
                                    />
                                    Enable reminders
                                </label>
                            </div>
                        </div>

                    </div>

                    {/* FOOTER */}
                    <div className={classes.modalFooter}>
                        <button
                            type="button"
                            className={classes.cancelBtn}
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={classes.createBtn}
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <FaSpinner className={classes.spinner} />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <FaCheckCircle /> Create Appointment
                                </>
                            )}
                        </button>
                    </div>
                </form>

            </div>
        </div>
    );
};

export default Newappointment;